/**
 * Phase 93 (Plan 93-01, Task 2) — module route guard.
 *
 * presentation-only: the real enforcement is the backend never registering a
 * disabled module's Fastify plugins (a genuine 404) — see `docs/private/MODULES.md`.
 * This guard deliberately does NOT redirect via a router-level location
 * change — a real 404 never changes the URL (research Pitfall 6), so a
 * disabled module renders `NotFoundPage` inline, in place, preserving the
 * attempted deep-link path.
 *
 * Trusts the fail-open `useModules().hasModule()` contract during loading —
 * it never null-renders while the query is in flight. `hasModule` is
 * optimistically `true` during that window (D-1), so children render
 * immediately; a null-while-loading branch would blank all 78 gated routes
 * for a network round-trip after every login.
 */
import type { ReactNode } from 'react';
import { useModules } from '../../services/modulesContext';
import { NotFoundPage } from '../../pages/NotFoundPage';
import type { ModuleId } from '../../services/api/modules';

export function RequireModule({ module, children }: { module: ModuleId; children: ReactNode }) {
  const { hasModule } = useModules();
  if (!hasModule(module)) {
    return <NotFoundPage />;
  }
  return <>{children}</>;
}
